import React, { useEffect,useState } from "react";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";

import { useActiveDocumentType } from "features/documents/hooks/useActiveDocumentType";
import { useMarkDocumentReviewed } from "features/documents/hooks/useMarkDocumentReviewed";
import { usePaginatedDocuments } from "features/documents/hooks/usePaginatedDocuments";
import {
  DeleteConfirmModal,
  EditDocumentModal,
  UploadDocumentModal,
} from "features/documents/modals";
import { DocumentsTable } from "features/documents/tables/DocumentsTable";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import {
  downloadDocument as downloadDocumentHelper,
  downloadDocumentsBulk,
  fetchDocumentForPreview,
} from "utils/documentDownload";

import { ChangeStatusModal } from "../../patient/PatientDocumentsPage/components/ChangeStatusModal/ChangeStatusModal";
import { DocumentPreviewModal } from "../../patient/PatientDocumentsPage/components/DocumentPreviewModal/DocumentPreviewModal";
import { SendEmailModal } from "../../patient/PatientDocumentsPage/components/SendEmailModal/SendEmailModal";
import { SendSmsModal } from "../../patient/PatientDocumentsPage/components/SendSmsModal/SendSmsModal";
import { useDocumentFilters } from "../../patient/PatientDocumentsPage/hooks/useDocumentFilters";
import { useDocumentPreview } from "../../patient/PatientDocumentsPage/hooks/useDocumentPreview";
import { useDocumentSelection } from "../../patient/PatientDocumentsPage/hooks/useDocumentSelection";
import { DocumentTypeMenu } from "./components/DocumentTypeMenu/DocumentTypeMenu";
import styles from "./DocumentsPage.module.css";

// A document's own name, escaped: it reaches the print header, the tab and the filename offered on
// "Save as PDF", and it comes from a user-supplied upload.
const printTitleOf = (doc) =>
  String(doc?.fileName || doc?.name || "Document").replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]
  );

const ROWS_OPTIONS = [10, 20, 50, 100];

const DocumentsPage = () => {
  const [ownershipFilter, setOwnershipFilter] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { documentTypes, isLoading: documentTypesLoading, activeType: activeDocumentCategory, setActiveType: setActiveDocumentCategory } = useActiveDocumentType();

  const selection = useDocumentSelection();

  const handleDocumentCategoryChange = (newCategory) => {
    setActiveDocumentCategory(newCategory);
    setCurrentPage(1);
    selection.deselectAll();
  };

  const handlePageChange = (_event, value) => {
    if (value !== currentPage) {
      selection.deselectAll();
    }
    setCurrentPage(value);
  };
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [deletingDocument, setDeletingDocument] = useState(null);
  const [isSmsModalOpen, setIsSmsModalOpen] = useState(false);
  const [smsDocument, setSmsDocument] = useState(null);
  const [isPreviewEmailModalOpen, setIsPreviewEmailModalOpen] = useState(false);
  const [previewEmailDocument, setPreviewEmailDocument] = useState(null);

  const tenant =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  const { data: staffInfo } = useStaffInfo();
  const currentUserStaffId = staffInfo?.staffMember?.id;

  const filters = useDocumentFilters([], { searchByPatient: true });

  const {
    data: paginatedData,
    isLoading: documentsFetching,
    refetch: refetchDocuments,
  } = usePaginatedDocuments({
    page: currentPage,
    rows: rowsPerPage,
    documentType: activeDocumentCategory === "ALL" ? undefined : activeDocumentCategory,
    patientName: filters.searchQuery || undefined,
    status: filters.statusFilter?.length ? filters.statusFilter : undefined,
    sortBy: filters.sortField,
    sortDirection: filters.sortDirection,
    mydocs: ownershipFilter === "MINE",
  });

  const allDocuments = paginatedData?.data || [];
  const totalRecords = paginatedData?.totalRecords ?? 0;

  const documents = allDocuments;

  const {
    previewData,
    document: previewDocument,
    openPreview,
    closePreview,
  } = useDocumentPreview();

  useEffect(() => {
    setCurrentPage(1);
  }, [
    filters.searchQuery,
    filters.statusFilter,
    filters.priorityFilter,
    ownershipFilter,
  ]);

  const handleUploadClick = () => {
    setIsUploadModalOpen(true);
  };

  const handleViewDocument = (document) => {
    const patientId = document.patient?.id || document.patientId;
    if (document.id && patientId) {
      openPreview(document.id, patientId);
    } else {
      toast.error("Document or patient information is missing");
    }
  };

  const handleDownloadDocument = async (document) => {
    const patientId = document?.patient?.id || document?.patientId;

    if (
      !document?.id ||
      !patientId ||
      !document?.fileName ||
      !document?.fileType
    ) {
      toast.error(
        "Document information is missing (id, patientId, fileName, or fileType)"
      );
      return;
    }

    try {
      await downloadDocumentHelper({
        documentId: document.id,
        patientId,
        tenant,
        fileName: document.fileName,
        fileType: document.fileType,
      });
      toast.success("Document downloaded successfully");
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Failed to download document");
    }
  };

  const handleEditDocument = (document) => {
    setEditingDocument(document);
    setIsEditModalOpen(true);
  };

  const handleDeleteDocument = (document) => {
    setDeletingDocument(document);
    setIsDeleteModalOpen(true);
  };

  const handleChangeStatus = (document) => {
    setEditingDocument(document);
    setIsChangeStatusModalOpen(true);
  };

  const { mutate: markDocumentReviewed } = useMarkDocumentReviewed({
    tenant,
    reviewerStaffId: currentUserStaffId,
    // Closed as on the patient page: the preview holds the document as it was opened, and
    // left open it would offer the action again on a document that is already reviewed.
    onSuccess: () => {
      refetchDocuments();
      closePreview();
    },
  });

  const handleReviewDocument = (doc) => {
    if (!doc?.id) {
      toast.error("Invalid document");

      return;
    }

    markDocumentReviewed(doc);
  };

  const handleBulkDownload = async () => {
    const selectedDocs = selection.selectedIds
      .map((id) => allDocuments.find((doc) => String(doc.id) === String(id)))
      .filter(Boolean);

    if (selectedDocs.length === 0) {
      toast.error("No documents selected for download");
      return;
    }

    const docsToDownload = selectedDocs.map((doc) => ({
      id: doc.id,
      patientId: doc.patient?.id || doc.patientId,
      fileName: doc.fileName,
      fileType: doc.fileType,
    }));

    try {
      const results = await downloadDocumentsBulk(docsToDownload, tenant);
      if (results.success > 0) {
        toast.success(`Downloaded ${results.success} document(s)`);
      }
      if (results.failed > 0) {
        toast.error(`Failed to download ${results.failed} document(s)`);
        console.error("Download errors:", results.errors);
      }
    } catch (error) {
      console.error("Error during bulk download:", error);
      toast.error("Failed to download documents");
    }
  };

  const handleBulkChangeStatus = () => {
    if (selection.selectedIds.length > 0) {
      setIsChangeStatusModalOpen(true);
    }
  };

  const handleSendEmailFromBulk = () => {
    if (selection.selectedIds.length > 0) {
      setIsEmailModalOpen(true);
    }
  };

  const handleSendEmail = (document) => {
    setPreviewEmailDocument(document);
    setIsPreviewEmailModalOpen(true);
  };

  const handleSendSMS = (document) => {
    setSmsDocument(document);
    setIsSmsModalOpen(true);
  };
  void handleSendSMS;

  const handlePrintDocument = async (document) => {
    const patientId = document?.patient?.id || document?.patientId;

    if (!document?.id || !patientId) {
      toast.error("Document information is missing");
      return;
    }

    try {
      const base64data = await fetchDocumentForPreview(
        document.id,
        patientId,
        tenant
      );

      if (base64data) {
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          printWindow.document.write(`
            <html>
              <head>
                <title>${printTitleOf(document)}</title>
                <style>
                  body { margin: 0; padding: 0; }
                  img { width: 100%; height: auto; }
                </style>
              </head>
              <body onload="window.print(); window.close();">
                <img src="${base64data}" />
              </body>
            </html>
          `);
          printWindow.document.close();
        }
      }
    } catch (error) {
      console.error("Error printing document:", error);
      toast.error("Failed to print document");
    }
  };

  return (
    <div className={styles.wrapper}>
      <DocumentTypeMenu
        selectedType={activeDocumentCategory}
        onTypeChange={handleDocumentCategoryChange}
        documentTypes={documentTypes}
        isLoading={documentTypesLoading}
      />

      <div className={styles.mainContainer}>
        <DocumentsTable
          documents={documents}
          totalDocuments={totalRecords}
          serverTotalCount={totalRecords}
          isLoading={documentsFetching}
          filters={{
            searchQuery: filters.searchQuery,
            setSearch: filters.setSearch,
            statusFilter: filters.statusFilter,
            onStatusFilterChange: filters.setStatus,
            priorityFilter: filters.priorityFilter,
            onPriorityFilterChange: filters.setPriority,
            sortField: filters.sortField,
            sortDirection: filters.sortDirection,
            onSortChange: filters.setSort,
          }}
          selection={selection}
          onUploadClick={handleUploadClick}
          onView={handleViewDocument}
          onDelete={handleDeleteDocument}
          onDownload={handleDownloadDocument}
          onEdit={handleEditDocument}
          onChangeStatus={handleChangeStatus}
          onReview={handleReviewDocument}
          onBulkChangeStatus={handleBulkChangeStatus}
          onSendEmail={handleSendEmail}
          onPrint={handlePrintDocument}
          onBulkSendEmail={handleSendEmailFromBulk}
          onBulkDownload={handleBulkDownload}
          documentType={activeDocumentCategory}
          ownershipFilter={ownershipFilter}
          onOwnershipFilterChange={setOwnershipFilter}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          rowsOptions={ROWS_OPTIONS}
          rowsValue={rowsPerPage}
          onRowsChange={(val) => { setRowsPerPage(val); setCurrentPage(1); }}
          onPageChange={handlePageChange}
        />
      </div>

      {isUploadModalOpen && (
        <UploadDocumentModal
          isOpen={isUploadModalOpen}
          allowPatientSelection={true}
          documentType={activeDocumentCategory}
          onClose={() => setIsUploadModalOpen(false)}
          onSuccess={() => {
            refetchDocuments();
            setIsUploadModalOpen(false);
          }}
        />
      )}

      {isEditModalOpen && editingDocument && (
        <EditDocumentModal
          isOpen={isEditModalOpen}
          document={editingDocument}
          allowPatientSelection={true}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingDocument(null);
          }}
          onSuccess={() => {
            refetchDocuments();
            setIsEditModalOpen(false);
            setEditingDocument(null);
          }}
        />
      )}

      {isChangeStatusModalOpen && (
        <ChangeStatusModal
          isOpen={isChangeStatusModalOpen}
          document={editingDocument}
          selectedIds={selection.selectedIds}
          onClose={() => {
            setIsChangeStatusModalOpen(false);
            setEditingDocument(null);
          }}
          onSuccess={() => {
            refetchDocuments();
            selection.deselectAll();
            setIsChangeStatusModalOpen(false);
            setEditingDocument(null);
          }}
        />
      )}

      {isDeleteModalOpen && deletingDocument && (
        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          document={deletingDocument}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setDeletingDocument(null);
          }}
          onSuccess={() => {
            refetchDocuments();
            setIsDeleteModalOpen(false);
            setDeletingDocument(null);
          }}
        />
      )}

      {previewDocument && (
        <DocumentPreviewModal
          isOpen={!!previewDocument}
          document={previewDocument}
          imageData={previewData}
          onClose={closePreview}
          onDownload={() => handleDownloadDocument(previewDocument)}
          onSendEmail={() => handleSendEmail(previewDocument)}
          onReview={handleReviewDocument}
        />
      )}

      {isSmsModalOpen && smsDocument && (
        <SendSmsModal
          isOpen={isSmsModalOpen}
          document={smsDocument}
          onClose={() => {
            setIsSmsModalOpen(false);
            setSmsDocument(null);
          }}
        />
      )}

      {isPreviewEmailModalOpen && previewEmailDocument && (
        <SendEmailModal
          isOpen={isPreviewEmailModalOpen}
          patient={previewEmailDocument.patient}
          documents={[previewEmailDocument]}
          onClose={() => {
            setIsPreviewEmailModalOpen(false);
            setPreviewEmailDocument(null);
          }}
        />
      )}

      {isEmailModalOpen && (
        <SendEmailModal
          isOpen={isEmailModalOpen}
          documents={selection.selectedIds
            .map((id) => {
              return allDocuments.find((doc) => String(doc.id) === String(id));
            })
            .filter(Boolean)}
          onClose={() => setIsEmailModalOpen(false)}
        />
      )}
    </div>
  );
};

export { DocumentsPage };